/**
 * 絞り込み用のプルダウン＋チェックボックス(複数選択可)。全ページのフィルターはこれで統一する(2026-09-10 指示)。
 *  - value が空 = 絞り込みなし(すべて)。選択があればその項目だけ表示
 *  - ボタンに「ラベル: すべて / 選択した名前 / N件」を出し、押すと一覧が開く。外側クリック・Esc で閉じる
 */
import { useEffect, useRef, useState } from 'react';
import { IconChevronDown } from '@core/components/icons';

export type MultiOption<K extends string = string> = { key: K; label: string };

/** value が空ならすべて一致。key が無い(null)行は、絞り込み中は除外 */
export function matchesFilter<K extends string>(value: Set<K>, key: K | null | undefined): boolean {
  if (value.size === 0) return true;
  return key !== null && key !== undefined && value.has(key);
}

export default function MultiSelect<K extends string>({
  label,
  options,
  value,
  onChange,
  allLabel = 'すべて',
}: {
  label: string;
  options: MultiOption<K>[];
  value: Set<K>;
  onChange: (next: Set<K>) => void;
  allLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = options.filter((o) => value.has(o.key));
  const summary = value.size === 0 ? allLabel : selected.length <= 2 ? selected.map((o) => o.label).join('、') : `${selected.length}件`;
  const toggle = (key: K) => {
    const next = new Set(value);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  };

  return (
    <div ref={ref} className={`msel${value.size > 0 ? ' on' : ''}`}>
      <button type="button" className="msel-btn" onClick={() => setOpen((v) => !v)} aria-haspopup="listbox" aria-expanded={open}>
        <span className="msel-label">{label}:</span> {summary}
        <span className="msel-caret">
          <IconChevronDown size={12} />
        </span>
      </button>
      {open && (
        <div className="msel-panel" role="listbox" aria-multiselectable="true">
          <label className="msel-item msel-all">
            <input type="checkbox" checked={value.size === 0} onChange={() => onChange(new Set())} />
            {allLabel}
          </label>
          {/* 一覧のすべてを選ぶ(2026-09-11 指示。既定の「すべて」= 絞り込みなし とは別) */}
          <label className="msel-item msel-all">
            <input type="checkbox" checked={value.size === options.length && options.length > 0} onChange={(e) => onChange(e.target.checked ? new Set(options.map((o) => o.key)) : new Set())} />
            全て選択({options.length})
          </label>
          {options.map((o) => (
            <label key={o.key} className="msel-item">
              <input type="checkbox" checked={value.has(o.key)} onChange={() => toggle(o.key)} />
              {o.label}
            </label>
          ))}
          {options.length === 0 && <div className="hint" style={{ padding: '6px 10px' }}>選択肢がありません</div>}
        </div>
      )}
    </div>
  );
}
